// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Helpers for reading the HITL `PlanApproval_v1` attributes bag.
 *
 * The attributes bag has no fixed schema in code (it's a free-form
 * `Record<string, unknown>` forwarded from the object store), so plan
 * narration "versions" are distinguished by attribute values, not by a
 * TypeScript discriminant. Today that's `optimizationType: "day-ahead"` +
 * `plan_version: "v1"` — the new drivers/actions narration template. Older
 * plans (or future versions) are mapped onto the same drivers/actions layout
 * (see `withStandardNarration`).
 */

import { browserTimeZone, nextDayKey, zonedDayKey } from './siteTime';

export type PlanAttributes = Record<string, unknown> | null | undefined;

/** True when this approval's attributes mark it as the day-ahead v1 narration. */
export function isDayAheadV1Plan(attributes: PlanAttributes): boolean {
  return attributes?.optimizationType === 'day-ahead' && attributes?.plan_version === 'v1';
}

/** Comma-separated `tags` attribute, split into trimmed, non-empty entries. */
export function getPlanTags(attributes: PlanAttributes): string[] {
  const raw = attributes?.tags;
  if (typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);
}

/** Reads a numeric attribute, tolerating numbers, numeric strings, null and undefined. */
export function getNumberAttr(attributes: PlanAttributes, key: string): number | undefined {
  const value = attributes?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/** Reads a string attribute, tolerating null/undefined/non-string values. */
export function getStringAttr(attributes: PlanAttributes, key: string): string | undefined {
  const value = attributes?.[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Parses a plan's `executionStart` to a Date.
 *
 * Accepts a full ISO timestamp (`2026-07-08T00:00:00+00:00`) or a bare
 * `YYYY-MM-DD`; the bare form is anchored to UTC midnight so it doesn't slide to
 * the previous day for anyone west of Greenwich. Returns undefined when the
 * attribute is missing or unparseable.
 */
export function parseExecutionStart(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/**
 * The plan's execution day as `YYYY-MM-DD`. `targetDate` is the plan's own
 * execution day and is authoritative; `executionStart` is the fallback for
 * older plans that predate it, read as the calendar date it was written with.
 * It is never converted: `2026-08-06T00:00:00+05:30` is Aug 6 for its site even
 * though it falls on Aug 5 in UTC. A plan with neither has no execution day.
 */
export function planExecutionDay(attributes: PlanAttributes): string | undefined {
  const targetDate = getStringAttr(attributes, 'targetDate');
  if (targetDate) return targetDate.slice(0, 10);
  const start = getStringAttr(attributes, 'executionStart');
  if (!start) return undefined;
  // "00:00"-style fixture values aren't dates; only ISO dates yield a day.
  const iso = start.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : undefined;
}

/** `YYYY-MM-DD` on the site's calendar, or the viewer's when it has no zone. */
function siteDayKey(d: Date, timeZone?: string): string {
  return zonedDayKey(d, timeZone ?? browserTimeZone());
}

/**
 * Whether the plan's execution day is already behind the site's today. A plan
 * with no execution day is not expired (there is nothing to compare), unlike
 * {@link executesTodayOrLater}, which drops those rows from a queue.
 */
export function executionDayPassed(
  attributes: PlanAttributes,
  now: Date = new Date(),
  timeZone?: string
): boolean {
  const executesOn = planExecutionDay(attributes);
  return executesOn !== undefined && executesOn < siteDayKey(now, timeZone);
}

/**
 * Whether the plan's execution window opens today or tomorrow — the operator's
 * "what am I running next" filter on Home › Recommendations.
 *
 * The plan's execution day ({@link planExecutionDay}) is compared against the
 * *site's* today/tomorrow: at 22:00 in Los Angeles an Atlanta site is already on
 * tomorrow.
 */
export function executesTodayOrTomorrow(
  attributes: PlanAttributes,
  now: Date = new Date(),
  timeZone?: string
): boolean {
  const executesOn = planExecutionDay(attributes);
  if (!executesOn) return false;
  const today = siteDayKey(now, timeZone);
  return executesOn === today || executesOn === nextDayKey(today);
}

/**
 * Whether the plan's execution day is the site's today — the Live tab's "what
 * was meant to run now" check when nothing is executing. Same
 * execution-day-vs-site-day comparison as {@link executesTodayOrTomorrow}.
 */
export function executesToday(
  attributes: PlanAttributes,
  now: Date = new Date(),
  timeZone?: string
): boolean {
  const executesOn = planExecutionDay(attributes);
  return executesOn !== undefined && executesOn === siteDayKey(now, timeZone);
}

/**
 * Whether the plan executes today or on any later day — the manager's "is this
 * still a live decision" filter on Home › Recommendations. Same
 * execution-day-vs-site-day comparison as {@link executesTodayOrTomorrow};
 * `YYYY-MM-DD` keys order correctly as plain strings.
 *
 * A plan with no execution day is excluded. Legacy plans predate
 * the attribute entirely, and a queue promising "executing today or later"
 * shouldn't carry rows whose execution day is unknown.
 */
export function executesTodayOrLater(
  attributes: PlanAttributes,
  now: Date = new Date(),
  timeZone?: string
): boolean {
  const executesOn = planExecutionDay(attributes);
  return executesOn !== undefined && executesOn >= siteDayKey(now, timeZone);
}
